// --- Biome & Upgrade Path Configuration ---
// The `path` arrays now only contain the keys, referencing the master list above.
/**
 * @file This file contains the configuration for different biomes and their upgrade paths.
 * @module biomesConfig
 */

/**
 * @typedef {Object} Animal
 * @property {string} key - The unique key for the animal.
 * @property {string} name - The display name of the animal.
 * @property {string} display - The emoji or character representing the animal.
 * @property {string} description - A brief description of the animal.
 */

/**
 * @typedef {Object} UpgradePathLinear
 * @property {string} key - The unique key for the upgrade path (e.g., 'simple', 'default', 'long', 'huge').
 * @property {string} name - The display name of the upgrade path.
 * @property {string[]} path - An array of item keys representing the progression in this path.
 */

/**
 * @typedef {Object} Biome
 * @property {string} name - The display name of the biome.
 * @property {string} cssClass - The CSS class associated with the biome for styling.
 * @property {string[]} bufferIcons - An array of emoji or characters used as buffer icons for the biome.
 * @property {string} bufferBackgroundColor - The background color for the biome's buffer.
 * @property {Animal[]} animals - An array of animal configurations for this biome.
 * @property {string} defaultPathKey - The key of the default upgrade path for this biome.
 * @property {UpgradePathLinear[]} upgradePathLinear - An array of available linear upgrade paths for this biome.
 */

/**
 * The main configuration object for all biomes.
 * Each key represents a biome type (e.g., 'default', 'jungle', 'city', 'futuristic').
 * @type {Object.<string, Biome>}
 */
// --- Biome & Upgrade Path Configuration ---
// The `path` arrays now only contain the keys, referencing the master list above.
export const biomesConfig = {
    default: {
        name: 'Woodland',
        cssClass: 'biome-woodland',
        bufferIcons: ['🍂', '🍁', '🍏', '🌳','🌲','🌴','🍃','🍄'],
        entranceSound: 'enter_woodland',
        bufferBackgroundColor: '#2c221c', // Darker wood
        animals: [
            { key: 'deer', name: 'Deer', display: '🦌', description: 'A graceful deer, often seen at the edge of the woods.' },
            { key: 'rabbit', name: 'Rabbit', display: '🐇', description: 'A swift rabbit, quick to dart into its burrow.' },
            { key: 'squirrel', name: 'Squirrel', display: '🐿️', description: 'A busy squirrel, always gathering nuts.' }
        ],
        defaultPathKey: 'default',
        upgradePathLinear: [
            { key: 'simple', name: 'Simple', path: [
                'w_0', 'w_2', 'w_4', 'w_5', 'w_6', 'w_10'
            ]},
            { key: 'default', name: 'Medium', path: [
                'w_0', 'w_1', 'w_2', 'w_3', 'w_4', 'w_5', 'w_6', 'w_7', 'w_8', 'w_9', 'w_10'
            ]},
            { key: 'long', name: 'Long', path: [
                'w_0', 'w_1', 'w_2', 'w_3', 'w_4', 'w_5', 'w_6', 'w_7', 'w_8', 'w_9', 'w_10', 'w_11', 'w_12', 'w_13', 'w_14', 'w_15'
            ]},
            { key: 'huge', name: 'Huge', path: [
                'w_0', 'w_1', 'w_2', 'w_3', 'w_4', 'w_5', 'w_6', 'w_7', 'w_8', 'w_9', 'w_10', 'w_11', 'w_12', 'w_13', 'w_14', 'w_15', 'w_16', 'w_17', 'w_18', 'w_19'
            ]}
        ]
    },
    jungle: {
        name: 'Jungle',
        cssClass: 'biome-jungle',
        bufferIcons: ['🦜', '🦎', '🌴','🌸'],
        entranceSound: 'enter_jungle',
        bufferBackgroundColor: '#11261c', // Darker jungle
        animals: [
            { key: 'tiger', name: 'Tiger', display: '🐅', description: 'A powerful tiger, master of the jungle shadows.' },
            { key: 'monkey', name: 'Monkey', display: '🐒', description: 'A playful monkey, swinging through the canopy.' },
            { key: 'snake', name: 'Snake', display: '🐍', description: 'A slithering snake, coiled and ready to strike.' }
        ],
        defaultPathKey: 'default',
        upgradePathLinear: [
            { key: 'simple', name: 'Simple', path: [
                'j_0', 'j_1', 'j_2', 'j_3', 'j_4', 'j_5'
            ]},
            { key: 'default', name: 'Medium', path: [
                'j_0', 'j_1', 'j_2', 'j_3', 'j_4', 'j_5', 'j_6', 'j_7', 'j_8', 'j_9', 'j_10'
            ]},
            { key: 'long', name: 'Long', path: [
                'j_0', 'j_1', 'j_2', 'j_3', 'j_4', 'j_5', 'j_6', 'j_7', 'j_8', 'j_9', 'j_10', 'j_11', 'j_12', 'j_13', 'j_14', 'j_15'
            ]},
            { key: 'huge', name: 'Huge', path: [
                'j_0', 'j_1', 'j_2', 'j_3', 'j_4', 'j_5', 'j_6', 'j_7', 'j_8', 'j_9', 'j_10', 'j_11', 'j_12', 'j_13', 'j_14', 'j_15', 'j_16', 'j_17', 'j_18', 'j_19'
            ]}
        ]
    },
    city: {
        name: 'City',
        cssClass: 'biome-city',
        bufferIcons: ['🏢', '🏨', '🏬', '🏛️'],
        entranceSound: 'enter_city',
        bufferBackgroundColor: '#111827', // Darker city
        animals: [
            { key: 'pigeon', name: 'Pigeon', display: '🕊️', description: 'A common pigeon, pecking for crumbs on the sidewalk.' },
            { key: 'rat', name: 'Rat', display: '🐀', description: 'A resourceful rat, scurrying through the alleyways.' },
            { key: 'raccoon', name: 'Raccoon', display: '🦝', description: 'A mischievous raccoon, a notorious trash bandit.' }
        ],
        defaultPathKey: 'default',
        upgradePathLinear: [
            { key: 'simple', name: 'Simple', path: [
                'c_0', 'c_1', 'c_2', 'c_3', 'c_4', 'c_5'
            ]},
            { key: 'default', name: 'Medium', path: [
                'c_0', 'c_1', 'c_2', 'c_3', 'c_4', 'c_5', 'c_6', 'c_7', 'c_8', 'c_9', 'c_10'
            ]},
            { key: 'long', name: 'Long', path: [
                'c_0', 'c_1', 'c_2', 'c_3', 'c_4', 'c_5', 'c_6', 'c_7', 'c_8', 'c_9', 'c_10', 'c_11', 'c_12', 'c_13', 'c_14','c_15'
            ]},
            { key: 'huge', name: 'Huge', path: [
                'c_0', 'c_1', 'c_2', 'c_3', 'c_4', 'c_5', 'c_6', 'c_7', 'c_8', 'c_9', 'c_10', 'c_11', 'c_12', 'c_13', 'c_14', 'c_15', 'c_16', 'c_17', 'c_18', 'c_19'
            ]}
        ]
    },
    futuristic: {
        name: 'Futuristic',
        cssClass: 'biome-futuristic',
        bufferIcons: ['🤖', '🚀', '🛰️', '✨', '🌌'],
        entranceSound: 'enter_futuristic',
        bufferBackgroundColor: '#0d0d2b',
        animals: [
            { key: 'xenomorph', name: 'Xenomorph', display: '👽', description: 'A bio-mechanical creature from the outer reaches.' },
            { key: 'crystalline_entity', name: 'Crystalline Entity', display: '💎', description: 'A lifeform based on silicon, shimmering with inner light.' },
            { key: 'gazer', name: 'Gazer', display: '👾', description: 'A mysterious floating entity, observing with its many eyes.' }
        ],
        defaultPathKey: 'default',
        upgradePathLinear: [
            { key: 'simple', name: 'Simple', path: [
                'f_0', 'f_1', 'f_2', 'f_3', 'f_4', 'f_5'
            ]},
            { key: 'default', name: 'Medium', path: [
                'f_0', 'f_1', 'f_2', 'f_3', 'f_4', 'f_5', 'f_6', 'f_7', 'f_8', 'f_9', 'f_10'
            ]},
            { key: 'long', name: 'Long', path: [
                'f_0', 'f_1', 'f_2', 'f_3', 'f_4', 'f_5', 'f_6', 'f_7', 'f_8', 'f_9', 'f_10', 'f_11', 'f_12', 'f_13', 'f_14','f_15'
            ]},
            { key: 'huge', name: 'Huge', path: [
                'f_0', 'f_1', 'f_2', 'f_3', 'f_4', 'f_5', 'f_6', 'f_7', 'f_8', 'f_9', 'f_10', 'f_11', 'f_12', 'f_13', 'f_14', 'f_15', 'f_16', 'f_17', 'f_18', 'f_19'
            ]}
        ]
    },
    birds: {
        name: 'Birds',
        cssClass: 'biome-birds',
        bufferIcons: ['🌾', '🏡', '🥚', '🌳', '🐥'],
        entranceSound: 'enter_birds',
        bufferBackgroundColor: '#8B4513', // Earthy brown
        animals: [
            { key: 'chicken', name: 'Chicken', display: '🐔', description: 'A common farm chicken, clucking around.' },
            { key: 'duck', name: 'Duck', display: '🦆', description: 'A waddling duck, enjoying the pond.' },
            { key: 'owl', name: 'Owl', display: '🦉', description: 'A wise owl, perched high in the trees.' }
        ],
        defaultPathKey: 'default',
        upgradePathLinear: [
            { key: 'simple', name: 'Simple', path: [
                'b_0', 'b_1', 'b_2', 'b_3', 'b_4', 'b_5'
            ]},
            { key: 'default', name: 'Medium', path: [
                'b_0', 'b_1', 'b_2', 'b_3', 'b_4', 'b_5', 'b_6', 'b_7', 'b_8', 'b_9', 'b_10'
            ]},
            { key: 'long', name: 'Long', path: [
                'b_0', 'b_1', 'b_2', 'b_3', 'b_4', 'b_5', 'b_6', 'b_7', 'b_8', 'b_9', 'b_10', 'b_11', 'b_12', 'b_13', 'b_14', 'b_15'
            ]},
            { key: 'huge', name: 'Huge', path: [
                'b_0', 'b_1', 'b_2', 'b_3', 'b_4', 'b_5', 'b_6', 'b_7', 'b_8', 'b_9', 'b_10', 'b_11', 'b_12', 'b_13', 'b_14', 'b_15', 'b_16', 'b_17', 'b_18', 'b_19'
            ]}
        ]
    }
};